// Builds the InputHandler callback table for GameScene. Lives apart so the
// scene file stays inside the 200-line cap. The scene supplies a small
// `SceneHooks` adapter that exposes mutable view state + the engine
// dispatchers; this module just wires it into the callback shape that
// `InputHandler` expects.

import {
  dispatchDigit,
  dispatchErase,
  dispatchRedo,
  dispatchUndo,
  moveSelection,
} from './SceneActions.js';
import { lightTap } from '../native/haptics.js';
import type { InputCallbacks } from '../input/InputHandler.js';
import type { GameStateData } from '../game/GameState.js';
import type { NonEmptyDigit } from '../game/types.js';

/** Minimal contract the dispatch table needs from the scene. The scene
 *  keeps the mutable view-state; this module only reads it via getters and
 *  pokes it via setters. */
export interface SceneHooks {
  readonly getState: () => GameStateData | null;
  readonly isFinished: () => boolean;
  /** True while the pause menu / end-game modal is up. Engine-mutating ops
   *  (digit, erase, undo, redo) must short-circuit so window-level keyboard
   *  events don't reach the engine through the modal. */
  readonly isPaused: () => boolean;
  readonly getSelectedIndex: () => number | null;
  readonly setSelectedIndex: (index: number | null) => void;
  readonly getPencilMode: () => boolean;
  readonly setPencilMode: (next: boolean) => void;
  readonly onDigitApplied: (cellIndex: number, wasError: boolean) => void;
  readonly onEngineMutated: () => void;
  readonly onNumpadDigitTap: (digit: NonEmptyDigit, entered: boolean) => void;
  readonly requestHint: () => void;
  readonly togglePause: () => void;
}

export function buildSceneCallbacks(hooks: SceneHooks): InputCallbacks {
  const digit = (d: NonEmptyDigit): void => {
    const state = hooks.getState();
    if (state === null || hooks.isFinished() || hooks.isPaused()) return;
    const selected = hooks.getSelectedIndex();
    const result = dispatchDigit(state, selected, d, hooks.getPencilMode());
    if (result.applied && selected !== null) {
      if (!result.wasError) lightTap();
      hooks.onDigitApplied(selected, result.wasError);
    }
    hooks.onNumpadDigitTap(d, result.applied);
  };
  const erase = (): void => {
    const state = hooks.getState();
    if (state === null || hooks.isFinished() || hooks.isPaused()) return;
    if (!dispatchErase(state, hooks.getSelectedIndex())) return;
    hooks.onEngineMutated();
  };
  const undoOp = (): void => {
    const state = hooks.getState();
    if (state === null || hooks.isFinished() || hooks.isPaused()) return;
    if (!dispatchUndo(state)) return;
    hooks.onEngineMutated();
  };
  const redoOp = (): void => {
    const state = hooks.getState();
    if (state === null || hooks.isFinished() || hooks.isPaused()) return;
    if (!dispatchRedo(state)) return;
    hooks.onEngineMutated();
  };
  const togglePencil = (): void => hooks.setPencilMode(!hooks.getPencilMode());

  return {
    onSelectCell: (idx) => hooks.setSelectedIndex(idx),
    onNumpadDigit: digit,
    onKeyDigit: digit,
    onNumpadPencilToggle: togglePencil,
    onKeyPencilToggle: togglePencil,
    onNumpadErase: erase,
    onKeyErase: erase,
    onNumpadUndo: undoOp,
    onNumpadHint: () => hooks.requestHint(),
    onPauseTap: () => hooks.togglePause(),
    onKeyUndo: undoOp,
    onKeyRedo: redoOp,
    onKeyArrow: (dir) =>
      hooks.setSelectedIndex(moveSelection(hooks.getSelectedIndex(), dir)),
    onKeyEscape: () => hooks.togglePause(),
  };
}
